import MoMusic from '@assets/animations/mo/moMusic.json';
import {Box, Typography} from '@mui/material';
import {motion} from 'framer-motion';
import Lottie from 'lottie-react';
import {useTranslation} from 'react-i18next';

import {CheckIcon} from '@/app/icons';
import {
  AppProgressButton,
  ButtonContainer,
  StepNumberIcon,
} from '@/app/shared-components';
import {COLORS} from '@/styles/colors';

import {KYC_APPROVED_ANIMATION, KYC_APPROVED_STEP_ICON} from './constants';
import {useViewModelProvider, ViewModelProvider} from './ViewModelContext';
import './styles';

const KycApprovedContent = () => {
  const {t} = useTranslation();
  const {handleDurationEnds, isProgressButtonVisible, steps} =
    useViewModelProvider();

  return (
    <Box className="kyc-approved-page-container">
      <Box className="kyc-approved-page-content">
        <motion.div
          className="kyc-approved-lottie-motion"
          initial={{
            opacity: 0,
            y: KYC_APPROVED_ANIMATION.LOTTIE_INITIAL_Y,
          }}
          animate={{opacity: 1, y: 0}}
          transition={{
            duration: KYC_APPROVED_ANIMATION.LOTTIE_DURATION,
            ease: 'easeOut',
          }}>
          <Lottie
            animationData={MoMusic}
            loop
            autoplay
            className="kyc-approved-lottie"
          />
        </motion.div>

        <motion.div
          initial={{opacity: 0}}
          animate={{opacity: 1}}
          transition={{
            delay: KYC_APPROVED_ANIMATION.TITLE_DELAY,
            duration: KYC_APPROVED_ANIMATION.TITLE_DURATION,
            ease: 'easeOut',
          }}>
          <Typography className="kyc-approved-title">
            {t('kyc.approved.title')}
          </Typography>
        </motion.div>

        <Box className="kyc-approved-steps-list">
          {steps.map((step, index) => {
            const badgeDelay =
              KYC_APPROVED_ANIMATION.ITEM_BADGE_BASE_DELAY +
              index * KYC_APPROVED_ANIMATION.ITEM_BADGE_STAGGER_DELAY;

            const textDelay =
              badgeDelay + KYC_APPROVED_ANIMATION.ITEM_TEXT_DELAY_OFFSET;

            return (
              <Box
                className="kyc-approved-step-item"
                key={step.id}>
                <Box className="kyc-approved-step-track">
                  <motion.div
                    className={`kyc-approved-step-badge ${
                      step.isCompleted ? 'is-completed' : ''
                    }`}
                    initial={{opacity: 0}}
                    animate={{opacity: 1}}
                    transition={{
                      delay: badgeDelay,
                      duration: KYC_APPROVED_ANIMATION.ITEM_BADGE_DURATION,
                      ease: 'easeOut',
                    }}>
                    {step.isCompleted ? (
                      <CheckIcon
                        width={KYC_APPROVED_STEP_ICON.ICON_SIZE}
                        height={KYC_APPROVED_STEP_ICON.ICON_SIZE}
                        color={COLORS.ON_SURFACE_1}
                      />
                    ) : (
                      <StepNumberIcon
                        number={step.number}
                        width={KYC_APPROVED_STEP_ICON.ICON_SIZE}
                        height={KYC_APPROVED_STEP_ICON.ICON_SIZE}
                      />
                    )}
                  </motion.div>

                  {step.showConnector && (
                    <motion.div
                      className="kyc-approved-step-connector"
                      initial={{opacity: 0}}
                      animate={{opacity: 1}}
                      transition={{
                        delay:
                          badgeDelay +
                          KYC_APPROVED_ANIMATION.ITEM_TEXT_DELAY_OFFSET,
                        duration: KYC_APPROVED_ANIMATION.ITEM_BADGE_DURATION,
                        ease: 'easeOut',
                      }}
                    />
                  )}
                </Box>

                <motion.div
                  className="kyc-approved-step-text-wrap"
                  initial={{
                    opacity: 0,
                    x: KYC_APPROVED_ANIMATION.ITEM_TEXT_INITIAL_X,
                  }}
                  animate={{opacity: 1, x: 0}}
                  transition={{
                    delay: textDelay,
                    duration: KYC_APPROVED_ANIMATION.ITEM_TEXT_DURATION,
                    ease: 'easeOut',
                  }}>
                  <Typography className="kyc-approved-step-text">
                    {step.label}
                  </Typography>
                </motion.div>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box className="button-container">
        <ButtonContainer sticky>
          {isProgressButtonVisible && (
            <motion.div
              initial={{
                opacity: 0,
                y: KYC_APPROVED_ANIMATION.CTA_FADE_UP_INITIAL_Y,
              }}
              animate={{opacity: 1, y: 0}}
              transition={{
                duration: KYC_APPROVED_ANIMATION.CTA_FADE_UP_DURATION,
                ease: 'easeOut',
              }}>
              <AppProgressButton
                label={t('kyc.approved.button')}
                progressDurationMs={
                  KYC_APPROVED_ANIMATION.CTA_PROGRESS_DURATION_MS
                }
                onProgressEnds={handleDurationEnds}
                onPress={handleDurationEnds}
              />
            </motion.div>
          )}
        </ButtonContainer>
      </Box>
    </Box>
  );
};

const KycApprovedPage = () => (
  <ViewModelProvider>
    <KycApprovedContent />
  </ViewModelProvider>
);

export default KycApprovedPage;
